import React, { useRef } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import cx from 'classnames';

import { backgroundImage, getTypeFromUrl } from 'utils/media';
import { FileType } from 'constants/constants';

import Scene, { selectSceneLibrary, MEDIA_UPLOAD_START, MEDIA_UPLOAD_SIG, MEDIA_UPLOAD, MEDIA_UPLOAD_COMPLETE } from 'store/models/Scene';
import UIState from 'store/UIState';

import Scrollbars from 'components/Scrollbars';
import { PlusIcon } from 'components/Svg';
import Button from 'components/Button';
import Menu, { MenuItem } from 'components/Menu';
import FileDrop from 'components/FileDrop';
import { ErrorList } from 'components/forms/Error';
import Spinner from 'components/Spinner';
import LoadingOverlay from 'components/LoadingOverlay';


// ===========================
// STORE
// ===========================
const selectors = createSelector(
  selectSceneLibrary,
  (library) => Object.assign({ library })
);

const actions = {
  fetchData: () => dispatch => dispatch(Scene.actions.getSceneLibrary()),
  uploadMedia: Scene.actions.uploadMedia,
  createScene: Scene.actions.createScene,
  showSuccessToast: UIState.actions.showSuccessToast,
  showErrorToast: UIState.actions.showErrorToast,
}
const withConnect = connect(selectors, actions);

// ===========================
// PARTIALS
// ===========================
const SceneItem = React.forwardRef(({ scene, disabled, className, onSceneClick,...props}, ref) => {
  let videoElm = useRef();
  let type = getTypeFromUrl(scene.url);
  let thumbnail;
  let onLoad = () => {
    videoElm.current.currentTime = (videoElm.current.duration ?? 0) * .10;
  }
  switch(type) {
    case FileType.VIDEO:
      thumbnail = (
        <div className="thumb video">
          <video controls={false} ref={videoElm} onLoadedData={onLoad}>
            <source src={scene.url} />
          </video>
        </div>
      );
      break;
    default:
      thumbnail = <div className="thumb image" style={backgroundImage(scene.url)} />;
  }
  return (
    <div 
      ref={ref}
      className={cx("g-library-item", className, { disabled })} 
      onClick = {() => onSceneClick?.(scene?._id, scene)}
      {...props}>
      { thumbnail }
      <div className="hover"> 
        <PlusIcon color="primary" size="l" />
      </div>
    </div>)
})


// ===========================
// COMPONENT
// ===========================
class SceneLibrary extends React.Component {
  state = {
    queuedFiles: null,
    errors: {},
    isUploading: false,
    currentUploadFile: null,
    uploadProgress: null,
  }

  componentDidMount() {
    this.props.fetchData();
  }

  onSceneAdd = (sceneId, scene) => {
    this.props.onSceneAdd?.(sceneId, scene);
  }
  onSceneDelete = sceneId => this.props.onSceneDelete?.(sceneId)
  onUploadClick = () => this.props.onUpload?.()

  onFileDrop = queuedFiles => {
    return this.setState({ queuedFiles }, () => this.onUploadMedia(_.first(queuedFiles)))
  }
  
  onUploadMedia = async (file, cb) => {
    let { user, uploadMedia, createScene, showSuccessToast, showErrorToast } = this.props;
    
    // Set uploading state
    this.setState({ 
      isUploading: true, 
      currentUploadFile: file,
      uploadProgress: MEDIA_UPLOAD_START
    });
    
    try {
      // Track upload progress through callback
      const uploadCallback = (stage, url, filename) => {
        this.setState({ uploadProgress: stage });
      };
      
      let tmpFilename = await uploadMedia(file, uploadCallback);
      if(!tmpFilename)
        throw new Error("File upload failed");

      let createRes = await createScene({ filename: tmpFilename, })
      if(!createRes)
        throw new Error("Creating scene failed.");

      // Show success notification
      showSuccessToast(`Successfully uploaded ${file.name}`);

      let queued = _.without(this.state.queuedFiles, file);
      this.setState({ 
        queuedFiles: queued,
        isUploading: queued.length > 0,
        currentUploadFile: null,
        uploadProgress: null,
      }, () => {
        if(queued.length) {
          this.onUploadMedia(_.first(queued));
        }
      });
      await this.props.fetchData();

    } catch(err) {
      console.log(err);
      showErrorToast(`Upload failed: ${err.message}`);
      this.setState({ 
        errors: _.castArray(err.message),
        isUploading: false,
        currentUploadFile: null,
        uploadProgress: null,
      });
    }
  }

  renderUploadStatus() {
    const { isUploading, currentUploadFile, uploadProgress } = this.state;
    if (!isUploading) return null;
    
    let statusText = "Uploading...";
    if (uploadProgress === MEDIA_UPLOAD_START) {
      statusText = "Preparing upload...";
    } else if (uploadProgress === MEDIA_UPLOAD_SIG) {
      statusText = "Getting upload permission...";
    } else if (uploadProgress === MEDIA_UPLOAD) {
      statusText = "Uploading file...";
    } else if (uploadProgress === MEDIA_UPLOAD_COMPLETE) {
      statusText = "Processing upload...";
    }
    
    return (
      <div className="upload-status">
        <Spinner size="small" />
        <div className="upload-status-text">
          {statusText}
          {currentUploadFile && <span> ({currentUploadFile.name})</span>}
        </div>
      </div>
    );
  }
  
  render() {
    let { library, disabledIds, filterIds, showBase = true, showDelete = true, className, } = this.props;
    let { errors, isUploading } = this.state;
    let { custom, base, archive } = library ?? {};
    return (
      <div className={cx("g-scene-library", className)}>
        <Scrollbars>
          <div className="add-slide">
            <ErrorList errors={errors} />
            {this.renderUploadStatus()}
            { !isUploading && <>
                <div className="item-title">Upload New Image</div>
                <FileDrop 
                  onDrop={this.onFileDrop}
                  disabled={isUploading}>
                  <PlusIcon color="sub" size="l" />
                </FileDrop>
              </>
            }
          </div>
          {!_.isEmpty(base) && showBase &&
          <div className="item-set">
            <div className="item-title">Slides: All Channels</div>
            <div className="item-list">
              {_.map(base, scene => (
                <SceneItem 
                  key={scene._id} 
                  scene={scene}
                  disabled={_.includes(disabledIds, scene._id)}
                  onSceneClick={this.onSceneAdd} />))}
            </div>
          </div>}

          {!_.isEmpty(custom) &&
          <div className="item-set">
            <div className="item-title">Slides: Targeted Channels</div>
            <div className="item-list">
              {_.map(custom, scene => (
                <SceneItem
                  key={scene._id}
                  scene={scene}
                  disabled={_.includes(disabledIds, scene._id)}
                  onSceneClick={this.onSceneAdd} />))}
            </div>
          </div>}

          {!_.isEmpty(archive) &&
          <div className="item-set">
            <div className="item-title">Unused Slides</div>
            <div className="item-list">
              { showDelete 
                ? _.map(archive, scene => (
                    <Menu
                    key={scene._id}
                    target={props => (
                      <SceneItem
                        {...props}
                        key={scene._id}
                        scene={scene}
                        className="archived"
                        disabled={_.includes(disabledIds, scene._id)} />)}>
                    <MenuItem onClick={() => this.onSceneAdd(scene._id)}>Add To Slideshow</MenuItem>
                    <MenuItem onClick={() => this.onSceneDelete(scene._id)}>Delete Scene</MenuItem>
                  </Menu>))
                : _.map(archive, scene => (
                    <SceneItem
                      key={scene._id}
                      scene={scene}
                      disabled={_.includes(disabledIds, scene._id)}
                      onSceneClick={this.onSceneAdd} />))
              }
            </div>
          </div>}
        </Scrollbars>
      </div>);
  }
};

export default withConnect(SceneLibrary);